import { GetServerSideProps } from 'next';
import { useState, useMemo } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { FiShoppingCart, FiSearch, FiChevronDown, FiChevronRight } from 'react-icons/fi';
import SEOHead from '../components/SEOHead';
import { useCart } from '../lib/CartContext';
import customToast from '../lib/toastUtils';
import { getShopCatalogData } from '../lib/commerce/catalog';

export type ShopProduct = {
  id: string;
  slug?: string;
  title: string;
  imageUrl: string;
  price: number;
  categoryId: string;
  categoryName: string;
  categorySlug: string;
  stock: number;
  active: boolean;
  parentName?: string;
  parentSlug?: string;
  discountPercent?: number | null;
};

export type ShopLabels = {
  shopTitle?: string;
  searchPlaceholder?: string;
  sortDefault?: string;
  sortAsc?: string;
  sortDesc?: string;
  categoryAll?: string;
  productsFound?: string;
  noProducts?: string;
  noProductsHint?: string;
  importProducts?: string;
  addToCart?: string;
  perPage?: string;
  itemsPerPage?: string;
  previous?: string;
  next?: string;
  page?: string;
};

export type CategoryGroup = { parent: string; children: { id: string; name: string; slug: string }[] };

type ShopProps = {
  products: ShopProduct[];
  categories: { id: string; name: string; slug: string }[];
  categoryTree?: CategoryGroup[];
  initialCategory: string;
  initialParent?: string;
  initialSearch?: string;
  labels?: ShopLabels;
  seoCanonicalUrl?: string;
  seoLang?: string;
};

const defaultLabels: Required<ShopLabels> = {
  shopTitle: 'Shop',
  searchPlaceholder: 'Search products...',
  sortDefault: 'Sort: Default',
  sortAsc: 'Price: Low to High',
  sortDesc: 'Price: High to Low',
  categoryAll: 'All',
  productsFound: 'products found',
  noProducts: 'No products found',
  noProductsHint: 'Try a different search or category.',
  importProducts: 'Import Products from CJ',
  addToCart: 'Add',
  perPage: 'Per page',
  itemsPerPage: 'items',
  previous: 'Previous',
  next: 'Next',
  page: 'Page',
};

const formatPrice = (p: number) =>
  `€${p.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;

const safeImage = (src: any): string => {
  if (typeof src === 'string') {
    const trimmed = src.trim();
    if (trimmed.startsWith('[')) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed) && typeof parsed[0] === 'string') {
          return parsed[0];
        }
      } catch {
        // fall through
      }
    }
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://') || trimmed.startsWith('/')) {
      return trimmed;
    }
  }
  return '/no-image.png';
};

function productPageHref(product: { id: string; slug?: string; title: string }): string {
  if (product.slug) return `/product/${product.slug}`;
  const slug = product.title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 60);
  return `/product/${slug ? `${slug}-${product.id.toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 8)}` : product.id}`;
}

const PER_PAGE_OPTIONS = [12, 24, 48, 96];

const toSlug = (value: string): string =>
  String(value || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const slugToTitle = (slug: string): string =>
  String(slug || '')
    .split('-')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

const getLeafName = (name: string): string => {
  const raw = String(name || '').trim();
  if (!raw) return '';
  const parts = raw.split('>').map((p) => p.trim()).filter(Boolean);
  return parts.length ? parts[parts.length - 1] : raw;
};

export default function Shop({ products, categories, categoryTree, initialCategory, initialParent, initialSearch, labels: labelsProp, seoCanonicalUrl, seoLang }: ShopProps) {
  const labels = { ...defaultLabels, ...labelsProp };
  const [search, setSearch] = useState((initialSearch || '').trim());
  const [activeCategory, setActiveCategory] = useState(initialCategory || 'all');
  const [activeParent, setActiveParent] = useState((initialParent || '').trim());
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  const [sort, setSort] = useState<'default' | 'asc' | 'desc'>('default');
  const [perPage, setPerPage] = useState(24);
  const [page, setPage] = useState(1);
  const { addProduct } = useCart();

  const useTree = Array.isArray(categoryTree) && categoryTree.length > 0;

  const headingTitle = useMemo(() => {
    if (activeParent) {
      const group = (categoryTree || []).find((g) => toSlug(g.parent) === activeParent);
      return group?.parent || slugToTitle(activeParent);
    }
    if (activeCategory && activeCategory !== 'all') {
      const byFlat = categories.find((c) => c.slug === activeCategory || c.id === activeCategory);
      if (byFlat?.name) return byFlat.name;
      for (const group of categoryTree || []) {
        const child = group.children.find((c) => c.slug === activeCategory || c.id === activeCategory);
        if (child?.name) return child.name;
      }
      return slugToTitle(activeCategory);
    }
    return 'Shop Electronics & Gadgets';
  }, [activeParent, activeCategory, categories, categoryTree]);

  const filtered = useMemo(() => {
    let list = products.filter((p) => p.active && p.stock > 0);
    if (activeParent) {
      const selectedGroup = (categoryTree || []).find((group) => toSlug(group.parent) === activeParent);
      if (selectedGroup) {
        const childKeys = new Set(
          selectedGroup.children.flatMap((child) => [String(child.slug || '').trim(), String(child.id || '').trim()])
        );
        list = list.filter((p) => childKeys.has(String(p.categorySlug || '').trim()) || childKeys.has(String(p.categoryId || '').trim()));
      } else {
        list = list.filter((p) => p.parentSlug === activeParent);
      }
    } else if (activeCategory !== 'all') {
      list = list.filter((p) => p.categorySlug === activeCategory || p.categoryId === activeCategory);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((p) => p.title.toLowerCase().includes(q));
    }
    if (sort === 'asc') list = [...list].sort((a, b) => a.price - b.price);
    if (sort === 'desc') list = [...list].sort((a, b) => b.price - a.price);
    return list;
  }, [products, activeCategory, activeParent, search, sort, categoryTree]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const pageSafe = Math.max(1, Math.min(page, totalPages));
  const paginated = useMemo(
    () => filtered.slice((pageSafe - 1) * perPage, pageSafe * perPage),
    [filtered, pageSafe, perPage]
  );

  const handleAddToCart = (product: ShopProduct) => {
    const base = product.price;
    const discount = product.discountPercent ?? 0;
    const finalPrice = discount > 0 ? base * (1 - discount / 100) : base;
    addProduct({
      id: product.id,
      title: product.title,
      price: finalPrice.toFixed(2),
      imageUrl: product.imageUrl,
      mode: 'payment',
      stripePriceId: product.id,
    });
    customToast.success(labels.addToCart === 'Add' ? 'Added to cart!' : 'Pridėta į krepšelį!', 'added-to-cart');
  };

  return (
    <>
      <SEOHead
        title={`${labels.shopTitle} — GadgetFever`}
        description="Browse our full collection of electronics, gadgets, smartphones, laptops, home electronics and wearables."
        canonicalUrl={seoCanonicalUrl ?? '/shop'}
        lang={seoLang ?? 'en'}
      />

      <main className="w-full min-h-screen bg-[#e8e8e8] dark:bg-black px-0 py-0">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="mb-6">
            <h1 className="text-2xl sm:text-3xl font-semibold text-gray-900 dark:text-white">{headingTitle}</h1>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">Browse smartphones, laptops, home electronics and wearables with secure checkout and EU shipping.</p>
          </div>
          <div className="flex flex-col lg:flex-row gap-8">
          {/* Left sidebar */}
          <aside className="w-full lg:w-64 flex-shrink-0 space-y-6">
            <div>
              <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-100 mb-2">{labels.shopTitle}</h2>
              <div className="relative">
                <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                  placeholder={labels.searchPlaceholder}
                  className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-gray-200 dark:border-[rgba(48,54,61,0.6)] bg-white dark:bg-[rgba(13,17,23,0.6)] text-sm text-gray-800 dark:text-gray-200 placeholder-gray-400 focus:outline-none focus:border-primary"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-100 mb-2">{labels.categoryAll}</label>
              <div className="space-y-1">
                <button
                  onClick={() => { setActiveCategory('all'); setActiveParent(''); setPage(1); }}
                  className={`flex w-full items-center gap-2 text-left px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                    activeCategory === 'all' && !activeParent
                      ? 'bg-primary text-white'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-white/10'
                  }`}
                >
                  {labels.categoryAll}
                </button>
                {useTree ? (
                  categoryTree.map((group) => {
                    const isOpen = openGroup === group.parent;
                    return (
                      <div key={group.parent || 'root'} className="rounded-lg overflow-hidden">
                        <button
                          type="button"
                          onClick={() => setOpenGroup(isOpen ? null : group.parent)}
                          className="flex w-full items-center justify-between gap-2 px-3 py-2 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-white/10 transition-all"
                        >
                          <span className="truncate">{group.parent || 'Other'}</span>
                          {isOpen ? <FiChevronDown className="w-4 h-4 flex-shrink-0" /> : <FiChevronRight className="w-4 h-4 flex-shrink-0" />}
                        </button>
                        {isOpen && (
                          <div className="pl-2 mt-0.5 space-y-0.5 border-l-2 border-gray-200 dark:border-gray-600 ml-2">
                            <button
                              onClick={() => { setActiveParent(toSlug(group.parent)); setActiveCategory('all'); setPage(1); }}
                              className={`block w-full text-left px-2 py-1.5 rounded-md text-sm font-medium transition-all ${
                                activeParent === toSlug(group.parent)
                                  ? 'bg-primary text-white'
                                  : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/10'
                              }`}
                            >
                              {group.parent}
                            </button>
                            {group.children.map((cat) => (
                              <button
                                key={cat.id}
                                onClick={() => { setActiveCategory(cat.slug); setActiveParent(''); setPage(1); }}
                                className={`block w-full text-left px-2 py-1.5 rounded-md text-sm font-medium transition-all ${
                                  activeCategory === cat.slug && !activeParent
                                    ? 'bg-primary text-white'
                                    : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/10'
                                }`}
                              >
                                {getLeafName(cat.name)}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })
                ) : (
                  categories.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => { setActiveCategory(cat.slug); setActiveParent(''); setPage(1); }}
                      className={`block w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                        activeCategory === cat.slug && !activeParent
                          ? 'bg-primary text-white'
                          : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-white/10'
                      }`}
                    >
                      {cat.name}
                    </button>
                  ))
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-100 mb-2">{labels.sortDefault}</label>
              <select
                value={sort}
                onChange={(e) => { setSort(e.target.value as 'default' | 'asc' | 'desc'); setPage(1); }}
                className="w-full px-3 py-2.5 rounded-xl border border-gray-200 dark:border-[rgba(48,54,61,0.6)] bg-white dark:bg-[rgba(13,17,23,0.6)] text-sm text-gray-700 dark:text-gray-200 focus:outline-none focus:border-primary"
              >
                <option value="default">{labels.sortDefault}</option>
                <option value="asc">{labels.sortAsc}</option>
                <option value="desc">{labels.sortDesc}</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-100 mb-2">{labels.perPage}</label>
              <select
                value={perPage}
                onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1); }}
                className="w-full px-3 py-2.5 rounded-xl border border-gray-200 dark:border-[rgba(48,54,61,0.6)] bg-white dark:bg-[rgba(13,17,23,0.6)] text-sm text-gray-700 dark:text-gray-200 focus:outline-none focus:border-primary"
              >
                {PER_PAGE_OPTIONS.map((n) => (
                  <option key={n} value={n}>{n} {labels.itemsPerPage}</option>
                ))}
              </select>
            </div>
          </aside>

          {/* Main content */}
          <div className="flex-1 min-w-0">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
              <p className="text-gray-500 dark:text-gray-300 text-sm">
                {filtered.length} {labels.productsFound}
              </p>
            </div>

            {filtered.length === 0 ? (
              <div className="text-center py-20">
                <p className="text-gray-400 dark:text-gray-300 text-lg mb-2">{labels.noProducts}</p>
                <p className="text-gray-500 dark:text-gray-400 text-sm mb-4">
                  {products.length === 0 ? labels.noProductsHint : labels.noProductsHint}
                </p>
                {products.length === 0 && (
                  <Link
                    href="/account/import"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary hover:bg-primaryDark text-white text-sm font-semibold transition-colors"
                  >
                    {labels.importProducts}
                  </Link>
                )}
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {paginated.map((product) => (
                    <div
                      key={product.id}
                      className="group relative bg-white dark:bg-[rgba(13,17,23,0.6)] rounded-2xl border border-gray-100 dark:border-[rgba(48,54,61,0.5)] overflow-hidden hover:shadow-xl hover:shadow-black/10 transition-all duration-300 hover:-translate-y-0.5 flex flex-col"
                    >
                      <Link href={productPageHref(product)} className="block aspect-square overflow-hidden bg-gray-50 dark:bg-white/5">
                        {product.imageUrl ? (
                          <Image
                            src={safeImage(product.imageUrl)}
                            alt={product.title}
                            width={300}
                            height={300}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-4xl">📦</div>
                        )}
                      </Link>
                      <div className="p-3 flex flex-col flex-1">
                        <Link href={productPageHref(product)}>
                          <h2 className="text-sm font-medium text-gray-800 dark:text-gray-200 line-clamp-2 mb-1 hover:text-primary transition-colors">
                            {product.title}
                          </h2>
                        </Link>
                        <p className="text-xs text-gray-400 mb-2">{product.categoryName}</p>
                        <div className="mt-auto flex items-center justify-between gap-2">
                          <div className="flex flex-col">
                            {product.discountPercent && product.discountPercent > 0 ? (
                              <>
                                <span className="text-[11px] text-gray-400 line-through">
                                  {formatPrice(product.price)}
                                </span>
                                <span className="font-bold text-gray-900 dark:text-white text-sm">
                                  {formatPrice(product.price * (1 - product.discountPercent / 100))}
                                </span>
                              </>
                            ) : (
                              <span className="font-bold text-gray-900 dark:text-white text-sm">
                                {formatPrice(product.price)}
                              </span>
                            )}
                          </div>
                          <button
                            onClick={() => handleAddToCart(product)}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary hover:bg-primaryDark text-white text-xs font-semibold transition-colors"
                          >
                            <FiShoppingCart className="w-3 h-3" />
                            {labels.addToCart}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="mt-8 flex flex-wrap items-center justify-center gap-2">
                    <button
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={pageSafe <= 1}
                      className="px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-600 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-white/5 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {labels.previous}
                    </button>
                    <span className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                      {labels.page} {pageSafe} / {totalPages}
                    </span>
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let p: number;
                      if (totalPages <= 5) p = i + 1;
                      else if (pageSafe <= 3) p = i + 1;
                      else if (pageSafe >= totalPages - 2) p = totalPages - 4 + i;
                      else p = pageSafe - 2 + i;
                      return (
                        <button
                          key={p}
                          onClick={() => setPage(p)}
                          className={`w-10 h-10 rounded-lg text-sm font-medium transition-colors ${
                            page === p
                              ? 'bg-primary text-white'
                              : 'border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-white/5'
                          }`}
                        >
                          {p}
                        </button>
                      );
                    })}
                    <button
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={pageSafe >= totalPages}
                      className="px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-600 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-white/5 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {labels.next}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        </div>
      </main>
    </>
  );
}

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const initialCategory = (ctx.query.category as string) || 'all';
  const initialParent = (ctx.query.parent as string) || '';
  const initialSearch = (ctx.query.q as string) || '';

  try {
    const { products, categories, categoryTree } = await getShopCatalogData();
    return {
      props: {
        products,
        categories,
        categoryTree,
        initialCategory,
        initialParent,
        initialSearch,
      },
    };
  } catch {
    // Catalog source unavailable
  }

  return {
    props: {
      products: [],
      categories: [],
      categoryTree: [],
      initialCategory,
      initialParent,
      initialSearch,
    },
  };
};
